<script lang="ts">
  import { Loader2, ChevronRight } from "lucide-svelte";
  import type { MessagePart, ToolCallPart } from "../../chat/types";
  import ThinkingBlock from "./ThinkingBlock.svelte";
  import ToolCallBlock from "./ToolCallBlock.svelte";
  import MarkdownContent from "../../chat/markdown-content.svelte";
  import { i18n } from "../../chat/i18n.svelte";

  const { t } = i18n;

  interface Props {
    parts: { part: MessagePart; messageId: string; isLast: boolean }[];
    isStreaming?: boolean;
  }

  let { parts, isStreaming = false }: Props = $props();
  let isExpanded = $state(false);

  // Auto-expand if any tool call is waiting for confirmation or user selection
  $effect(() => {
    const hasPending = parts.some(p =>
      p.part.type === 'toolCall' &&
      (p.part.status === 'awaiting_confirmation' || p.part.status === 'awaiting_selection')
    );
    if (hasPending && !isExpanded) {
        isExpanded = true;
    }
  });
</script>

<div style="
  margin: 4px 0 10px;
  border: 1px solid var(--chat-bg-tertiary);
  border-radius: var(--chat-radius);
  overflow: hidden;
  background: var(--chat-bg-secondary);
  box-shadow: var(--chat-shadow-sm);
  font-family: var(--chat-font-sans);
">
  <button
    type="button"
    onclick={() => (isExpanded = !isExpanded)}
    style="
      width: 100%;
      display: flex; align-items: center; justify-content: space-between;
      padding: 7px 10px;
      background: transparent;
      border: none; cursor: pointer;
      color: var(--chat-text-muted);
      transition: background 0.12s;
    "
    onmouseenter={(e) => ((e.currentTarget as HTMLElement).style.background = 'var(--chat-bg-tertiary)')}
    onmouseleave={(e) => ((e.currentTarget as HTMLElement).style.background = 'transparent')}
  >
    <div style="display: flex; align-items: center; gap: 8px;">
      <ChevronRight size={14} style="
        flex-shrink: 0;
        color: var(--chat-accent);
        transition: transform 0.2s cubic-bezier(0.1, 0.9, 0.2, 1);
        transform: {isExpanded ? 'rotate(90deg)' : 'none'};
      " />
      <span style="font-size: 12px; font-weight: 700; letter-spacing: -0.01em; color: var(--chat-brand-green);">{t("analysisStep")}</span>
      {#if parts.length > 0}
        <span style="
          padding: 1px 7px;
          background: var(--chat-accent-subtle);
          color: var(--chat-accent);
          border-radius: var(--chat-radius-pill);
          font-size: 11px; font-weight: 700;
        ">{parts.length}</span>
      {/if}
    </div>
    {#if isStreaming}
      <span class="thinking-loader-spin" aria-hidden="true" style="color: var(--chat-accent);">
        <Loader2 size={12} />
      </span>
    {/if}
  </button>

  {#if isExpanded}
    <div style="
      border-top: 1px solid var(--chat-bg-tertiary);
      padding: 12px 10px;
      display: flex; flex-direction: column; gap: 10px;
      max-height: 480px; overflow-y: auto;
      background: var(--chat-bg);
    ">
      {#each parts as entry, i (i)}

        {#if entry.part.type === "thinking" && entry.part.thinking?.trim()}
          <ThinkingBlock thinking={entry.part.thinking} isStreaming={isStreaming && entry.isLast} />
        {:else if entry.part.type === "toolCall"}
          <ToolCallBlock part={entry.part as ToolCallPart} />
        {:else if entry.part.type === "text" && entry.part.text?.trim()}
          <div style="
            padding: 6px 10px;
            font-size: 12px;
            color: var(--chat-text-muted);
            font-style: italic;
            line-height: 1.55;
            font-family: var(--chat-font-sans);
            border-left: 2px solid var(--chat-border-active);
            margin-left: 4px;
            flex-shrink: 0;
          ">
            <MarkdownContent text={entry.part.text || ""} />
          </div>
        {/if}
      {/each}
    </div>
  {/if}
</div>
